if (!customElements.get('menu-categorias')) {
  // Same breakpoints as assets/section-menu-categorias.css, widest first.
  const BREAKPOINTS = [
    ['D', '(min-width: 1200px)'],
    ['L', '(min-width: 990px)'],
    ['T', '(min-width: 750px)'],
    ['M', 'all'],
  ];

  // Brand logos (.menu-categorias--marcas), at every width: an endless strip that moves by itself and follows the
  // finger, a mouse drag and the trackpad. Copies of the logos go after the originals (hidden from screen readers and
  // from the keyboard), and the strip jumps back one repeat whenever it passes it, which nobody sees because the copy
  // is identical. While this runs, the section has .menu-categorias--loop (assets/section-menu-categorias.css);
  // without it, the strip scrolls.
  class MarcasLoop {
    static SPEED = 0.03; // px per ms when idle (30px/s)
    static MIN_LOGOS = 8; // fewer logos than this: no loop

    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('.menu-categorias__viewport');
      this.track = root.querySelector('[data-mc-track]');
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.x = 0;
      this.v = 0;
      this.dragEnd = -Infinity;
      // The logo size and the page width change with the screen: sync() decides again whether the strip loops.
      new ResizeObserver(() => this.sync()).observe(root);
      // Moves only while the strip is near the screen; the logos load all at once the first time.
      this.observer = new IntersectionObserver(([entry]) => this.onScreen(entry.isIntersecting), {
        rootMargin: '200px 0px',
      });

      const viewport = this.viewport;
      viewport.addEventListener('pointerdown', (event) => this.down(event));
      viewport.addEventListener('pointermove', (event) => this.move(event));
      viewport.addEventListener('pointerup', (event) => this.up(event));
      viewport.addEventListener('pointercancel', (event) => this.up(event, true));
      // A drag that ends on a logo must not open the brand.
      viewport.addEventListener(
        'click',
        (event) => {
          if (performance.now() > this.dragEnd + 400) return;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
      viewport.addEventListener('dragstart', (event) => this.on && event.preventDefault());
      // Trackpad (or Shift + wheel): a sideways swipe moves the strip; an up or down one still scrolls the page.
      viewport.addEventListener(
        'wheel',
        (event) => {
          if (!this.on || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
          event.preventDefault();
          this.x -= event.deltaMode === 1 ? event.deltaX * 16 : event.deltaX;
          this.v = 0;
        },
        { passive: false }
      );
      // Stops under the mouse and while a logo has the keyboard focus.
      viewport.addEventListener('pointerenter', (event) => (this.hover = event.pointerType === 'mouse'));
      viewport.addEventListener('pointerleave', () => (this.hover = false));
      this.track.addEventListener('focusin', (event) => this.focus(event.target));
      this.track.addEventListener('focusout', () => (this.focused = false));
    }

    sync() {
      const logos = this.track.querySelectorAll(':scope > [data-mc-item]');
      // One repeat (half the logos, in two rows) must be as wide as the page, or the strip would show its end.
      const step = (logos[0]?.offsetWidth || 0) + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
      const fits = Math.ceil(logos.length / 2) * step >= this.root.clientWidth;
      if (!this.root.isConnected || logos.length < MarcasLoop.MIN_LOGOS || !fits) return this.stop();
      this.start();
      this.measure(); // the logos change size at 750px
    }

    start() {
      if (this.on) return;
      this.on = true;
      const items = [...this.track.querySelectorAll(':scope > [data-mc-item]')];
      this.count = items.length;
      // Two rows fill column by column: with an odd number of logos, the pattern repeats only after two copies.
      const copies = this.count % 2 ? 2 : 1;
      for (let copy = 0; copy < copies; copy++) {
        for (const item of items) {
          const clone = item.cloneNode(true);
          clone.removeAttribute('data-mc-item');
          clone.setAttribute('data-mc-copy', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelector('a')?.setAttribute('tabindex', '-1');
          this.track.append(clone);
        }
      }
      this.copies = copies;
      this.root.classList.add('menu-categorias--loop');
      this.measure();
      this.observer.observe(this.root);
    }

    // One repeat: from the first logo to the copy that starts the same pattern (0 while the section is hidden).
    measure() {
      const kids = this.track.children;
      this.period = kids[this.count * this.copies].offsetLeft - kids[0].offsetLeft;
    }

    stop() {
      if (!this.on) return;
      this.on = false;
      this.observer.disconnect();
      this.onScreen(false);
      this.track.querySelectorAll(':scope > [data-mc-copy]').forEach((clone) => clone.remove());
      this.track.style.transform = '';
      this.root.classList.remove('menu-categorias--loop');
      this.x = 0;
      this.v = 0;
    }

    onScreen(visible) {
      if (visible && !this.frame) {
        if (!this.loaded) {
          this.loaded = true;
          this.track.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
        }
        this.last = 0;
        this.frame = requestAnimationFrame((now) => this.tick(now));
      } else if (!visible && this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
    }

    tick(now) {
      const dt = Math.min(now - (this.last || now), 64);
      this.last = now;
      if (!this.drag) {
        // The speed eases toward its target: a soft start and stop, and the glide after a fling.
        const still = this.held || this.hover || this.focused || this.reduce.matches;
        const target = still ? 0 : -MarcasLoop.SPEED;
        this.v += (target - this.v) * (1 - Math.exp(-dt / 350));
        this.x += this.v * dt;
      }
      // Keep the strip within one repeat, so there is always a copy on both sides.
      if (!(this.period > 0)) this.measure();
      if (!this.focused && this.period > 0) this.x = ((this.x % this.period) - this.period) % this.period;
      this.paint();
      this.frame = requestAnimationFrame((time) => this.tick(time));
    }

    paint() {
      this.track.style.transform = `translate3d(${this.x}px, 0, 0)`;
    }

    down(event) {
      if (!this.on || !event.isPrimary || event.button > 0) return;
      this.pointer = event.pointerId;
      this.held = true;
      this.startX = this.lastX = event.clientX;
      this.samples = [[event.timeStamp, event.clientX]];
    }

    move(event) {
      if (event.pointerId !== this.pointer) return;
      if (!this.drag) {
        if (Math.abs(event.clientX - this.startX) < 6) return;
        this.drag = true;
        this.v = 0;
        this.viewport.setPointerCapture(event.pointerId);
      }
      this.x += event.clientX - this.lastX;
      this.lastX = event.clientX;
      this.samples.push([event.timeStamp, event.clientX]);
      if (this.samples.length > 5) this.samples.shift();
    }

    // Letting go: the strip keeps the speed of the last moves (a fling) and eases back to its own pace.
    up(event, cancelled) {
      if (event.pointerId !== this.pointer) return;
      this.pointer = null;
      this.held = false;
      if (!this.drag) return;
      this.drag = false;
      this.dragEnd = performance.now();
      const [time, x] = this.samples[0];
      const elapsed = event.timeStamp - time;
      this.v = !cancelled && elapsed > 0 && elapsed < 200 ? (event.clientX - x) / elapsed : 0;
    }

    // The strip is not a scroll box, so the browser does not bring a focused logo into view: the strip moves to it.
    focus(target) {
      const item = this.on && target.closest('[data-mc-item]');
      if (!item) return;
      this.focused = true;
      this.v = 0;
      this.viewport.scrollLeft = 0;
      const view = this.viewport.getBoundingClientRect();
      const box = item.getBoundingClientRect();
      const margin = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
      if (box.left < view.left + margin) this.x += view.left + margin - box.left;
      else if (box.right > view.right - margin) this.x -= box.right - (view.right - margin);
      this.paint();
    }
  }

  customElements.define(
    'menu-categorias',
    class MenuCategorias extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-mc-track]');
        if (!this.track) return;
        if (!this.initialized) this.init();
        this.resizeObserver.observe(this.track);
        this.refresh();
        if (this.classList.contains('menu-categorias--marcas')) {
          this.loop ||= new MarcasLoop(this);
          this.loop.sync();
        }
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.loop?.stop();
      }

      init() {
        this.initialized = true;
        this.page = 0;
        this.prevButton = this.querySelector('[data-mc-prev]');
        this.nextButton = this.querySelector('[data-mc-next]');
        this.dotsList = this.querySelector('[data-mc-dots]');

        this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
        this.prevButton?.addEventListener('click', () => this.goTo(this.page - 1));
        this.nextButton?.addEventListener('click', () => this.goTo(this.page + 1));
        this.dotsList?.addEventListener('click', (event) => {
          const dot = event.target.closest('[data-page]');
          if (dot) this.goTo(Number(dot.dataset.page));
        });
        // Keyboard: a focused tile on another page brings its whole page into view
        // (the browser alone would scroll just enough to show the tile, between two pages).
        this.track.addEventListener('focusin', (event) => {
          requestAnimationFrame(() => this.showItem(event.target));
        });
        // Theme editor: selecting a block brings its page into view.
        this.addEventListener('shopify:block:select', (event) => this.showItem(event.target));
        this.resizeObserver = new ResizeObserver(() => this.refresh());
      }

      // The number of pages at the current breakpoint, written by Liquid in --mc-pages-*.
      get pageCount() {
        const value = parseInt(getComputedStyle(this.track).getPropertyValue('--mc-pages'), 10);
        return value > 0 ? value : 1;
      }

      // Distance between the starts of two pages: the visible width plus one column gap.
      get pageStep() {
        const style = getComputedStyle(this.track);
        const inner = this.track.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
        return inner + (parseFloat(style.columnGap) || 0);
      }

      onScroll() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }

      refresh() {
        const count = this.pageCount;
        if (count !== this.dotCount) this.renderDots(count);
        this.update();
      }

      renderDots(count) {
        this.dotCount = count;
        if (!this.dotsList) return;
        const { label = '', of = '/' } = this.dotsList.dataset;
        const dots = count > 1 ? Array.from({ length: count }, (_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'menu-categorias__dot';
          dot.dataset.page = index;
          dot.setAttribute('aria-label', `${label} ${index + 1} ${of} ${count}`.trim());
          dot.setAttribute('aria-controls', this.track.id);
          dot.append(document.createElement('span'));
          return dot;
        }) : [];
        this.dotsList.replaceChildren(...dots);
        this.dotsList.hidden = dots.length === 0;
      }

      update() {
        const count = this.pageCount;
        const step = this.pageStep;
        const page = step > 0 ? Math.min(count - 1, Math.max(0, Math.round(this.track.scrollLeft / step))) : 0;
        this.page = page;
        // Reveal before hiding, so an arrow that disappears can hand its focus to the other one.
        const arrows = [
          [this.prevButton, page > 0, this.nextButton],
          [this.nextButton, page < count - 1, this.prevButton],
        ];
        arrows.filter(([, visible]) => visible).forEach(([button]) => this.toggle(button, true));
        arrows.filter(([, visible]) => !visible).forEach(([button, , other]) => this.toggle(button, false, other));
        this.dotsList?.querySelectorAll('[data-page]').forEach((dot, index) => {
          dot.setAttribute('aria-current', index === page ? 'true' : 'false');
        });
      }

      // Shows or hides an arrow; an arrow that disappears while focused hands the focus on.
      toggle(button, visible, other) {
        if (!button || button.hidden === !visible) return;
        const hadFocus = document.activeElement === button;
        button.hidden = !visible;
        if (!hadFocus) return;
        const next = other && !other.hidden ? other : this.track.querySelector('a');
        next?.focus({ preventScroll: true });
      }

      goTo(page) {
        const target = Math.max(0, Math.min(this.pageCount - 1, page));
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({ left: target * this.pageStep, behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      // The page of a tile at the current breakpoint (data-page-m/t/l/d, written by Liquid).
      pageOf(item) {
        const [key] = BREAKPOINTS.find(([, query]) => window.matchMedia(query).matches);
        return Number(item.dataset[`page${key}`]) || 0;
      }

      showItem(element) {
        // One page (the brand logos scroll freely): the browser already shows the focused tile.
        if (this.pageCount < 2) return;
        const item = element?.closest?.('[data-mc-item]');
        if (item) this.goTo(this.pageOf(item));
      }
    }
  );
}
